import type { ChildId } from './valueObjects/childId';
import type { ClassId } from './valueObjects/classId';
import type { HealthRecordType } from './valueObjects/healthRecordType';
import type { HideChildNotebook } from './valueObjects/hideChildNotebook';
import type { HideCondition } from './valueObjects/hideCondition';
import type { Name } from './valueObjects/name';
import type { ParentId } from './valueObjects/parentId';
import type { ClassChildJsonPub, ClassChildJsonPvt } from '@/types/domain/class/entities/classChildEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * クラス子プロパティ非公開
 */
export type ClassChildPropsPvt = {
  /** クラスID */
  classId: ClassId,
  /** クラス名 */
  className: Name,
  /** 子ID */
  childId: ChildId,
  /** 親ID */
  parentId: ParentId,
  /** 連絡帳を使用しない */
  hideChildNotebook?: HideChildNotebook,
  /** 機嫌を表示しない */
  hideCondition?: HideCondition,
  /** クラス連絡帳内容設定 */
  childNotebookContents?: HealthRecordType[],
}

/**
 * クラス個プロパティ公開
 */
export type ClassChildPropsPub = Record<string, never>

/**
 * クラス子Entity
 */
export class ClassChildEntity extends EntityPub<
  ClassChildPropsPvt,
  ClassChildJsonPvt,
  ClassChildPropsPub,
  ClassChildJsonPub
> {
  /** 削除タイプ */
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  /** 削除キー */
  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonPubData(): ClassChildJsonPub {
    return {};
  }

  /** ラベル名 */
  static get labelName() {
    return 'クラス名';
  }

  toJsonData(): ClassChildJsonPvt {
    return {
      classId: this.classId.value,
      className: this.className.value,
      childId: this.childId.value,
      parentId: this.parentId.value,
      hideChildNotebook: this.hideChildNotebook?.value || false,
      hideCondition: this.hideCondition?.value || false,
      childNotebookContents: this.childNotebookContents?.map((v) => v.value),
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    return EntityName.WagakoNurseryClassClassChildEntity as EntityName;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return this.parentChildId;
  }

  /**
   * サブキー
   */
  get subKey(): string | null {
    return `${this.classId.value}#${this.parentChildId}`;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 親ID
   */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 子ID
   */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }

  /**
   * クラスID
   */
  get classId(): ClassId {
    return this._value.classId;
  }

  /**
   * クラスID
   */
  set classId(classId: ClassId) {
    this._value.classId = classId;
  }

  /**
   * クラス名
   */
  get className(): Name {
    return this._value.className;
  }

  /**
   * クラス名
   */
  set className(className: Name) {
    this._value.className = className;
  }

  /**
   * 連絡帳を使用しない
   */
  get hideChildNotebook(): HideChildNotebook | undefined {
    return this._value.hideChildNotebook;
  }

  /**
   * 連絡帳を使用しない
   */
  set hideChildNotebook(hideChildNotebook: HideChildNotebook | undefined) {
    this._value.hideChildNotebook = hideChildNotebook;
  }

  /**
   * 機嫌を表示しない
   */
  get hideCondition(): HideCondition | undefined {
    return this._value.hideCondition;
  }

  /**
   * 機嫌を表示しない
   */
  set hideCondition(hideCondition: HideCondition | undefined) {
    this._value.hideCondition = hideCondition;
  }

  /**
   * クラス連絡帳内容設定
   */
  get childNotebookContents(): HealthRecordType[] | undefined {
    return this._value.childNotebookContents;
  }

  /**
   * クラス連絡帳内容設定
   */
  set childNotebookContents(childNotebookContents: HealthRecordType[]) {
    this._value.childNotebookContents = childNotebookContents;
  }
}
